import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { ErrorView } from '@percent/admin-dashboard/common/components'
import { useStaticLinksAccess } from '@percent/admin-dashboard/common/hooks'
import { ExternalLink } from '@percent/admin-dashboard/common/components/externalLink/ExternalLink'
import {
  Badge,
  Button,
  Flex,
  Icon,
  Menu,
  Spacer,
  Table,
  TableBody,
  TableCell,
  TableProvider,
  TableRow,
  Typography
} from '@percent/lemonade'
import { StaticLinksProps } from './StaticLinks.types'
import style from './StaticLinks.module.scss'

export function StaticLinks({
  staticLinks,
  isLoading,
  errorMessage,
  partnerId,
  configurationNames,
  onEditClick,
  onDeleteClick
}: StaticLinksProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { canCreate, canUpdate, canDelete } = useStaticLinksAccess()

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  const columns = [
    { id: 'slug', content: t('typography.slug'), props: { width: '20%' } },
    { id: 'redirectUrl', content: t('typography.redirectUrl'), props: { width: '25%' } },
    { id: 'enabled', content: t('typography.status'), props: { width: '10%' } },
    { id: 'configurationName', content: t('typography.configurationName'), props: { width: '25%' } },
    { id: 'createdAt', content: t('typography.createdAt'), props: { width: '10%' } },
    { id: 'actions', props: { width: '5%' }, content: ' ' }
  ]

  const goToAddNewStaticLink = () => {
    navigate(`/partners/${partnerId}/static-links/create`)
  }

  const getMenuSections = (staticLink: { id: string; slug: string }) => [
    {
      items: [
        ...(canUpdate
          ? [
              {
                key: 'edit',
                label: t('button.edit'),
                onClick: () => onEditClick(staticLink)
              }
            ]
          : []),
        ...(canDelete
          ? [
              {
                key: 'delete',
                label: t('button.delete'),
                onClick: () => onDeleteClick(staticLink)
              }
            ]
          : [])
      ]
    }
  ]

  return (
    <TableProvider
      columns={columns}
      totalResults={staticLinks?.length ?? 0}
      isLoading={isLoading}
      illustration="no-results"
      emptyTableTitle={t('typography.noStaticLinksFound')}
      emptyTableDescription={t('typography.noStaticLinksDescription')}
    >
      <Flex align="center" justify="space-between">
        <Typography variant="h3">{t('typography.staticLinks')}</Typography>
        {canCreate && (
          <Button variant="secondary" size="small" onClick={goToAddNewStaticLink}>
            {t('button.addNewStaticLink')}
          </Button>
        )}
      </Flex>
      <Spacer size={4} axis="horizontal" />
      <Table>
        <TableBody>
          {staticLinks?.map(row => (
            <TableRow key={row.id}>
              <TableCell>{row.slug}</TableCell>
              <TableCell className={style.tableCell}>
                {row.redirectUrl ? <ExternalLink url={row.redirectUrl} /> : 'n/a'}
              </TableCell>
              <TableCell>
                {row.isEnabled ? (
                  <Badge variant="positive">{t('typography.enabled')}</Badge>
                ) : (
                  <Badge variant="default">{t('typography.disabled')}</Badge>
                )}
              </TableCell>
              <TableCell className={style.tableCell}>
                <Link to={`/partners/${partnerId}/programs/${row.validationSubmissionConfigurationId}/details`}>
                  {configurationNames[row.validationSubmissionConfigurationId] ||
                    row.validationSubmissionConfigurationId}
                </Link>
              </TableCell>
              <TableCell>{format(new Date(row.createdAt), 'dd MMM yyyy, HH:mm')}</TableCell>
              <TableCell>
                <Menu
                  sections={getMenuSections({ id: row.id, slug: row.slug })}
                  customTriggerComponent={
                    <span
                      role="button"
                      tabIndex={0}
                      style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                      data-testid="menu-verticalIcon-button"
                    >
                      <Icon name="menu-vertical" size="m" color="gray300" />
                    </span>
                  }
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableProvider>
  )
}
